import { DialogV2 } from '@recruit-robin/robin-theme/web-components';
import { memo, useEffect, useState } from 'react';

import type { CampaignPhoneReachOutDialogProps } from './CampaignPhoneReachOutDialog.types';

import { useCandidateCampaignQuery } from 'hooks/shared/useCandidateCampaignQuery';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { getDialogKeyByPageContext } from 'shared/legacy/contexts/DialogContext/getDialogKeyByPageContext';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { useGlobalDialogsStore } from 'stores/global-dialogs/global-dialogs.store';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialogConfig } from 'stores/global-dialogs/hooks/useGlobalDialogConfig';
import { useGlobalDialogData } from 'stores/global-dialogs/hooks/useGlobalDialogData';
import { useIsGlobalDialogOpen } from 'stores/global-dialogs/hooks/useIsGlobalDialogOpen';
import { getPageContext } from 'utils/getPageContext';
import { CampaignPhoneReachOutDialogSkeleton } from './CampaignPhoneReachOutDialog.skeleton';
import { useStyles } from './CampaignPhoneReachOutDialog.styles';
import { useAutomaticallyOpenDialog } from './hooks/useAutomaticallyOpenDialog';
import { useBoundaries } from './hooks/useBoundaries';
import { DialogStep, useDialogStep } from './hooks/useDialogStep';
import { usePosition } from './hooks/usePosition';
import { useSave } from './hooks/useSave';
import { useSelect } from './hooks/useSelect';
import { useSendUserNotification } from './hooks/useSendUserNotification';
import { CallOutcomeScreen } from './screens/CallOutcomeScreen';
import { FollowUpAlternativeChannelsScreen } from './screens/FollowUpAlternativeChannelsScreen';

const CampaignPhoneReachOutDialogImpl = ({
  isVisible,
  candidate,
  notes,
  wasAutomaticallyOpened,
}: CampaignPhoneReachOutDialogProps) => {
  const classes = useStyles();
  const [note, setNote] = useState('');
  const { currentStep, goToNextStep } = useDialogStep();
  const pageContext = usePageContext();

  const outcomes = useSelect({ isVisible, candidate });

  const { initialPosition, debouncedHandlePositionChange } = usePosition({ isVisible });
  const { handleSave, isSaving } = useSave({ note, candidate });
  const boundariesRef = useBoundaries();

  const campaignPhoneReachOutConfig = useGlobalDialogConfig(GlobalDialogType.CampaignPhoneReachOut);

  useSendUserNotification({ candidate, wasAutomaticallyOpened });

  useEffect(() => {
    if (isVisible && note !== notes) {
      const dialogKey = getDialogKeyByPageContext(
        GlobalDialogType.CampaignPhoneReachOut,
        getPageContext(),
        candidate.campaignId.toString(),
      );
      useGlobalDialogsStore.getState().set(
        GlobalDialogType.CampaignPhoneReachOut,
        {
          campaignId: candidate.campaignId,
          esPersonId: candidate.es_person_id,
          notes: note,
        },
        dialogKey,
      );
    }
  }, [note, isVisible, notes, candidate.campaignId, candidate.es_person_id]);

  useEffect(() => {
    if (isVisible) {
      // Only set notes when modal first opens, don't overwrite user input
      if (notes && note === '') {
        setNote(notes);
      }

      return;
    }

    outcomes.reset();
    setNote('');
  }, [isVisible, notes]);

  const handleBeforeSave = ({
    skipAlternativeChannels,
    newLabels,
  }: { skipAlternativeChannels?: boolean; newLabels?: string[] } = {}) => {
    if (
      campaignPhoneReachOutConfig.config.skipFollowUp ||
      pageContext === PageContext.CentralInbox ||
      skipAlternativeChannels ||
      outcomes.selected.includes('interviewScheduled')
    ) {
      handleSave({ newLabels: newLabels || outcomes.selected });

      return;
    }

    goToNextStep();
  };

  const handleFinishFollowUpActions = ({ hasFollowUp }: { hasFollowUp?: boolean } = {}) => {
    return handleSave({ newLabels: outcomes.selected, hasFollowUp });
  };

  return (
    <DialogV2.Root key={pageContext} isVisible={isVisible} customCss={classes.root}>
      <DialogV2.Draggable
        initialPosition={initialPosition}
        onPositionChange={debouncedHandlePositionChange}
        boundariesRef={boundariesRef}
      />

      <DialogV2.Content customCss={classes.content}>
        {currentStep === DialogStep.CALL_OUTCOME && (
          <CallOutcomeScreen
            note={note}
            setNote={setNote}
            outcomes={outcomes}
            onSave={handleBeforeSave}
            isSaving={isSaving}
            candidate={candidate}
          />
        )}

        {currentStep === DialogStep.FOLLOW_UP_ACTIONS && (
          <FollowUpAlternativeChannelsScreen
            candidate={candidate}
            onFinish={handleFinishFollowUpActions}
            isLoading={isSaving}
          />
        )}
      </DialogV2.Content>
    </DialogV2.Root>
  );
};

const availableInPages = [
  PageContext.Shared,
  PageContext.Candidate,
  PageContext.Campaign,
  PageContext.RatedCandidates,
  PageContext.CentralInbox,
];

export const CampaignPhoneReachOutDialog = memo(() => {
  const isVisible = useIsGlobalDialogOpen(GlobalDialogType.CampaignPhoneReachOut);
  const pageContext = usePageContext();
  const { data: currentUser } = useCurrentUserQuery();
  const isAvailable = availableInPages.includes(pageContext) && !!currentUser;

  const candidateData = useGlobalDialogData({ isVisible, dialogType: GlobalDialogType.CampaignPhoneReachOut });
  const { data: candidate, isFetching } = useCandidateCampaignQuery(
    !isVisible ? {} : { campaignId: candidateData?.campaignId, esPersonId: candidateData?.esPersonId },
    { enabled: isVisible && isAvailable },
  );

  const { wasAutomaticallyOpened } = useAutomaticallyOpenDialog(candidate);

  if (!isAvailable) return null;
  if (isFetching) return <CampaignPhoneReachOutDialogSkeleton isVisible />;
  if (!candidate) return null;

  return (
    <CampaignPhoneReachOutDialogImpl
      key={`${candidate.es_person_id}-${pageContext}`}
      isVisible={isVisible}
      candidate={candidate}
      notes={candidateData?.notes}
      wasAutomaticallyOpened={wasAutomaticallyOpened}
    />
  );
});
